import React, { useCallback, useState, useEffect } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import logger from "../services/Logger";
import Go from "./scheduleOptions/Go";
import TextAgent from "./scheduleOptions/TextAgent";
import IndividualPresent from "./scheduleOptions/IndividualPresent";

const type = "DraggableRow";

const DraggableRow = ({ index, moveRow, children }) => {
  const [{ isDragging }, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: type,
    hover: (item, monitor) => {
      if (!monitor.canDrop()) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const rowStyle = {
    background: isOver ? "#fffde7" : "transparent", // Only highlight when being hovered over
    boxShadow: isOver ? "0 0 5px rgba(0, 0, 0, 0.1)" : "none",
    transform: isOver ? "scale(1.01)" : "scale(1)",
    zIndex: isOver ? 998 : 1,
    transition: "background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
    position: "relative",
    cursor: "move",
    borderTop: isOver ? "2px dashed #bdbdbd" : "none",
    borderBottom: isOver ? "2px dashed #bdbdbd" : "1px solid #ddd",
    opacity: isDragging ? 0.4 : 1, // Make the dragged row semi-transparent instead of highlighted
  };

  return (
    <tr ref={(node) => drag(drop(node))} style={rowStyle}>
      {children}
    </tr>
  );
};

const DragAndDropTable = ({ columns, dataSet }) => {
  const [data, setData] = useState(dataSet);
  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      const newData = [...data];
      const draggedRow = newData[dragIndex];
      newData.splice(dragIndex, 1);
      newData.splice(hoverIndex, 0, draggedRow);
      setData(newData);
    },
    [data, setData]
  );

  useEffect(() => {
    logger.info("Setting data - ", dataSet);
    setData(dataSet);
  }, [dataSet]);

  if ((dataSet && dataSet.length === 0) || !data) {
    return <div>No data</div>;
  }

  // Function to render action components based on site requirements
  const getActionComponents = (site) => {
    const requirements = site.requirements;
    logger.debug("This is the requirement returned - ", requirements);

    const AgentOrOwnwer = "Agent or Owner";
    return (
      <div>
        {requirements.includes("Go") && <Go />}
        {requirements.includes("Text Listing Agent") && <TextAgent number={site.contactInfo} arrivalTime={site.arrivalTime} address={site.address} />}
        {requirements.includes("Agent or Owner Present") && <IndividualPresent individual={AgentOrOwnwer} />}
      </div>
    );
  };

  if (dataSet.length === 0 || !data) {
    return <div>No data</div>;
  }

  // Add the Actions column to the columns array
  const enhancedColumns = [
    ...columns,
    {
      Header: "Actions",
      id: "actions",
      Cell: ({ row }) => getActionComponents(row.original),
    },
  ];

  const tableStyle = {
    width: "100%",
    borderCollapse: "separate", // Changed from collapse to separate to allow for borders
    borderSpacing: 0,
    border: "1px solid #ddd",
  };

  const thStyle = {
    padding: "12px 8px",
    backgroundColor: "#f8f9fa",
    borderBottom: "2px solid #ddd",
    textAlign: "left",
  };

  const tdStyle = {
    padding: "8px",
    borderBottom: "1px solid #ddd",
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <table style={tableStyle}>
        <thead>
          <tr>
            {enhancedColumns.map((column, index) => (
              <th key={index} style={thStyle}>
                {column.Header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <DraggableRow key={index} index={index} moveRow={moveRow}>
              {enhancedColumns.map((column, colIndex) => (
                <td key={colIndex} style={tdStyle}>
                  {column.Cell ? column.Cell({ row: { original: row } }) : row[column.accessor]}
                </td>
              ))}
            </DraggableRow>
          ))}
        </tbody>
      </table>
    </DndProvider>
  );
};

export default DragAndDropTable;
